import { ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, HostListener, inject, Input, OnChanges, Output, QueryList, SimpleChanges, TemplateRef, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { CdkOverlayOrigin, Overlay, OverlayRef, ScrollStrategy, OverlayModule } from '@angular/cdk/overlay';

@Component({
  selector: 'lib-input-select',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule, MatIconModule, OverlayModule],
  templateUrl: './input-select.html',
  styleUrl: './input-select.scss',
})

export class InputSelect implements OnChanges, DoCheck {

  private element = inject(ElementRef);
  private cdref = inject(ChangeDetectorRef);

  //#region Inputs & Outputs
  @Input() Readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() masterList: any[] = [];
  @Input() selectedValue: any = 0;
  @Input() IsSelectedValueName: boolean = false;
  @Input() IsChip: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() inputLabel: string = "";
  @Input() ClearSelectedValue: boolean = false;
  @Input() showClearButton: boolean = true;

  @Output() selectedValueChange = new EventEmitter<any>();
  @Output() selectedValueName = new EventEmitter<any>();
  // @Output() masterListChange = new EventEmitter<DropdownItem[]>();

  //#endregion

  SelectedItem(masterList: DropdownItem[], selectedValue: any) {
    if (!masterList || !Array.isArray(masterList)) {
      this.selectedItem = "";
      return;
    }
    this.selectedItem = masterList.find(item => item.id == selectedValue)?.name || "";
  }

  //#region Variables
  selectedItem: string = "";
  isPanelOpen = false;
  OriginalList: DropdownItem[] = [];
  filteredList: DropdownItem[] = [];
  @ViewChildren("dropdownItem") dropdownItems!: QueryList<ElementRef>;
  highlightedIndex = -1;
  ChipsList: DropdownItem[] = [];
  isFocused = false;
  private originalMap = new Map<string | number, DropdownItem>();
  SelectAll = false;
  SelectFiltered = false;
  openAbove = false;
  IsFiltered = true;
  overlayWidth: number = 0;
  //#endregion

  //#region Lifecycle Hooks
  ngOnChanges(changes: SimpleChanges) {
    if (changes['Readonly']?.currentValue) {
      this.isPanelOpen = false;
    }

    if (changes["masterList"]?.currentValue && !changes['Readonly']?.currentValue) {

      const fullList = changes["masterList"].currentValue;
      // Filter out inactive items (is_active is false or 0)
      this.OriginalList = fullList.filter((item: any) =>
        item.is_active === undefined || item.is_active === null || item.is_active === true || item.is_active === 1 || item.is_active === '1'
      );
      this.filteredList = [...this.OriginalList];
      this.originalMap.clear();
      this.OriginalList.forEach(item => this.originalMap.set(item.id, item));
      if (this.ClearSelectedValue) {
        this.selectedValue = 0;
        this.selectedItem = "";
        this.isFocused = false;

        this.selectedValueChange.emit(this.selectedValue);
      }
    }

    if (!changes['selectedValue']?.currentValue) {
      this.isFocused = false
    }

    if (changes['IsChip']?.currentValue || (this.IsChip && changes["masterList"])) {
      this.ChipsList = [...this.OriginalList.filter((item: any) => item.IsChipSelected)];
      this.IsFiltered = this.ChipsList.length > 0 ? false : true;
    }

    if (changes["masterList"]) {
      this.applyFilters();
    }
  }

  ngDoCheck() {
    if (this.IsChip) {
      this.ChipsList = [...this.OriginalList.filter((item: any) => item.IsChipSelected)];
      this.IsFiltered = this.ChipsList.length > 0 ? false : true;
    }

    if (!this.IsChip) this.SelectedItem(this.masterList, this.selectedValue);

  }

  //#endregion

  //#region Event Listeners
  // onDocumentClick removed as cdkConnectedOverlay handles outside clicks now

  @HostListener("keyup", ["$event"])
  keypress(event: KeyboardEvent) {
    if (["Escape", "Enter"].includes(event.key)) {
      this.isPanelOpen = false;
    }
  }
  //#endregion

  //#region Dropdown Methods
  @HostListener('window:resize')
  onResize() {
    if (this.isPanelOpen) {
      const triggerEl = this.element.nativeElement.querySelector('.--input-select-group');
      if (triggerEl) {
        this.overlayWidth = triggerEl.getBoundingClientRect().width;
      }
    }
  }

  onPanelOpen() {
    if (this.Readonly || this.disabled) return;

    this.applyFilters('');

    const triggerEl = this.element.nativeElement.querySelector('.--input-select-group');
    if (triggerEl) {
      this.overlayWidth = triggerEl.getBoundingClientRect().width;
    }

    setTimeout(() => {
      this.cdref.detectChanges();
      this.isPanelOpen = true;
      this.calculateDropdownDirection();
      this.isFocused = !this.selectedItem;
      this.IsFiltered = this.ChipsList.length === 0;
      if (this.SelectFiltered && this.IsChip) this.onSelectFiltered();
    });

    this.filteredList = this.selectedItem ? this.filteredList : [...this.OriginalList];

    if (!this.IsChip && this.selectedValue) {
      this.highlightedIndex = this.filteredList.findIndex(item => item.id === this.selectedValue);
    } else if (this.ChipsList.length > 0 && this.IsChip) {
      this.highlightedIndex = this.filteredList.findIndex(item => item.IsChipSelected);
    } else {
      this.filteredList = this.OriginalList;
    }

    this.scrollHighlightedIntoView();
  }

  scrollHighlightedIntoView() {
    const items = this.dropdownItems?.toArray();
    const item = items?.[this.highlightedIndex];

    item?.nativeElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredList.length;
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.highlightedIndex = (this.highlightedIndex - 1 + this.filteredList.length) % this.filteredList.length;
    } else if (["Enter"].includes(event.key)) {
      this.selectItem(this.filteredList[this.highlightedIndex]);
    }

    this.scrollHighlightedIntoView();
  }
  //#endregion

  //#region Selection Methods
  // selectItem(item: DropdownItem) {
  //   if (!item) return;

  //   if (this.IsChip) {
  //     item.IsChipSelected = !item.IsChipSelected;
  //     this.ChipsList = this.OriginalList.filter(i => i.IsChipSelected);
  //     this.IsFiltered = this.ChipsList.length === 0;
  //     this.SelectFiltered = !this.IsFiltered && this.SelectFiltered;
  //     this.selectedItem = "";

  //     const selectedIds = this.ChipsList.map(i => i.id).join(",");
  //     this.selectedValue = selectedIds;
  //     this.selectedValueChange.emit(this.selectedValue);
  //   } else {
  //     this.selectedItem = item.name;
  //     this.selectedValue = item.id;
  //     this.selectedValueChange.emit(this.selectedValue);
  //     this.isPanelOpen = false;
  //   }
  // }

  async selectItem(item: DropdownItem) {
    if (!item) return;

    if (this.IsChip) {
      item.IsChipSelected = !item.IsChipSelected;
      this.ChipsList = this.OriginalList.filter(i => i.IsChipSelected);
      this.IsFiltered = this.ChipsList.length === 0;
      this.SelectFiltered = !this.IsFiltered && this.SelectFiltered;
      this.selectedItem = "";

      const selectedIds = this.ChipsList.map(i => i.id).join(",");
      this.selectedValue = selectedIds;

      await this.selectedValueChange.emit(this.selectedValue);

      // ✅ Emit only if IsChipSelected is used
      // this.masterListChange.emit([...this.OriginalList]);
    } else {
      this.selectedItem = item.name;
      this.selectedValue = item.id;
      if (this.IsSelectedValueName) this.selectedValueName.emit(item.name);
      await this.selectedValueChange.emit(this.selectedValue);
      this.isPanelOpen = false;
    }
  }

  // onSelectAll() {
  //   this.filteredList.forEach(item => (item.IsChipSelected = this.SelectAll));
  //   this.ChipsList = this.OriginalList.filter(i => i.IsChipSelected);

  //   const selectedIds = this.ChipsList.map(i => i.id).join(",");
  //   this.selectedValue = selectedIds;
  //   this.selectedValueChange.emit(this.selectedValue);
  // }

  onSelectAll() {
    this.filteredList.forEach(item => (item.IsChipSelected = this.SelectAll));
    this.ChipsList = this.OriginalList.filter(i => i.IsChipSelected);

    const selectedIds = this.ChipsList.map(i => i.id).join(",");
    this.selectedValue = selectedIds;
    if (this.IsChip && !this.ChipsList.length) {
      this.SelectFiltered = false;
    }
    this.selectedValueChange.emit(this.selectedValue);

    // this.masterListChange.emit([...this.OriginalList]);
  }

  onSelectFiltered() {
    this.applyFilters();
  }
  //#endregion

  //#region Filter Methods
  onFilter(term: string) {
    this.applyFilters(term);
  }

  applyFilters(term: string = '') {
    const lower = (term || '').toLowerCase();

    this.filteredList = this.OriginalList.filter(item => {
      const matchesSearch = !lower || item.name.toLowerCase().includes(lower);
      const matchesFiltered = !this.SelectFiltered || item.IsChipSelected;
      return matchesSearch && matchesFiltered;
    });

    this.highlightedIndex = this.filteredList.length > 0 ? 0 : -1;
    this.scrollHighlightedIntoView();
  }
  //#endregion

  //#region Utility Methods
  dropdownStyle: any = {};

  calculateDropdownDirection() {
    const inputEl: HTMLElement = this.element.nativeElement.querySelector("input");
    const rect = inputEl.getBoundingClientRect();

    // Calculate position relative to the document, not the viewport
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    // Check available space below
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 200; // Estimated height, or get actual element height if possible

    this.dropdownStyle = {
      'position': 'absolute',
      'left': '0', // Relative to the container component
      'width': '100%', // Match input width
      'z-index': '1000' // Ensure it's above other elements
    };

    if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
      this.openAbove = true;
      // Position above: negative bottom or explicit top
      // For absolute positioning relative to input wrapper:
      this.dropdownStyle.bottom = '100%';
      this.dropdownStyle.top = 'auto';
    } else {
      this.openAbove = false;
      this.dropdownStyle.top = '100%';
      this.dropdownStyle.bottom = 'auto';
    }
  }

  // onClear() {
  //   this.selectedValue = this.IsChip ? "" : 0;
  //   this.selectedItem = "";
  //   this.highlightedIndex = -1;
  //   this.ChipsList = [];
  //   this.OriginalList.forEach(item => (item.IsChipSelected = false));
  //   this.selectedValueChange.emit(this.selectedValue);
  //   this.onPanelOpen();
  // }

  // removeChip(item: DropdownItem) {
  //   const matchedItem = this.originalMap.get(item.id);
  //   if (matchedItem) {
  //     matchedItem.IsChipSelected = false;
  //     this.ChipsList = this.ChipsList.filter(chip => chip.id !== item.id);
  //     const selectedIds = this.ChipsList.map(i => i.id).join(",");
  //     this.selectedValue = selectedIds;
  //     this.selectedValueChange.emit(this.selectedValue);
  //   }
  // }

  onClear() {
    this.selectedValue = this.IsChip ? "" : 0;
    this.selectedItem = "";
    this.highlightedIndex = -1;
    this.ChipsList = [];
    this.OriginalList.forEach(item => (item.IsChipSelected = false));
    this.selectedValueChange.emit(this.selectedValue);

    // if (this.IsChip) {
    //   this.masterListChange.emit([...this.OriginalList]);
    // }

    this.onPanelOpen();

    if (this.ClearSelectedValue) {
      this.selectedValue = 0;
      this.selectedItem = "";
      this.isFocused = false;
      this.selectedValueChange.emit(this.selectedValue);
    }
  }

  removeChip(item: DropdownItem) {
    const matchedItem = this.originalMap.get(item.id);
    if (matchedItem) {
      matchedItem.IsChipSelected = false;
      this.ChipsList = this.ChipsList.filter(chip => chip.id !== item.id);

      const selectedIds = this.ChipsList.map(i => i.id).join(",");
      this.selectedValue = selectedIds;
      this.selectedValueChange.emit(this.selectedValue);

      // this.masterListChange.emit([...this.OriginalList]);
    }
  }

  // onClearFilter() {
  //   this.originalMap.forEach(item => (item.IsChipSelected = false));
  //   this.ChipsList = [];
  //   this.SelectFiltered = false;
  //   this.SelectAll = false;
  //   this.selectedValue = this.IsChip ? "" : 0;
  //   this.selectedValueChange.emit(this.selectedValue);
  //   this.onPanelOpen();
  // }
  onClearFilter() {
    this.originalMap.forEach(item => (item.IsChipSelected = false));
    this.ChipsList = [];
    this.SelectFiltered = false;
    this.SelectAll = false;
    this.selectedValue = this.IsChip ? "" : 0;
    this.selectedValueChange.emit(this.selectedValue);

    // if (this.IsChip) {
    //   this.masterListChange.emit([...this.OriginalList]);
    // }

    this.onPanelOpen();
  }

  //#endregion
}

interface DropdownItem {
  id: number | string;
  name: string;
  IsChipSelected?: boolean;
  statusLabel?: string;
  statusClass?: string;
  dotColor?: string;
}
